import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAppStore } from '../store/appStore';
import type { Account } from '../store/appStore';

export const SuggestionForm: React.FC = () => {
  const { handle } = useParams<{ handle: string }>();
  const location = useLocation();
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    hasFollowed,
    setHasFollowed,
    addSuggestion,
    fetchAccountByHandle,
  } = useAppStore();

  const isQR = location.search.includes('source=qr');

  useEffect(() => {
    setHasFollowed(false);
  }, [setHasFollowed]);

  useEffect(() => {
    const loadAccount = async () => {
      if (!handle) {
        setError('Ugyldig lenke');
        setIsLoading(false);
        return;
      }

      try {
        const acc = await fetchAccountByHandle(handle);
        if (!acc) {
          setError('Konto ikke funnet');
          setIsLoading(false);
          return;
        }
        setAccount(acc);
        setIsLoading(false);
      } catch {
        setError('Kunne ikke laste inn informasjon');
        setIsLoading(false);
      }
    };

    loadAccount();
  }, [handle, fetchAccountByHandle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || !message.trim() || !account) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await addSuggestion(
        account.id,
        message.trim(),
        name.trim() || null,
        isQR ? 'qr' : 'direct'
      );
      setMessage('');
      setName('');
      setShowSuccess(true);
    } catch {
      setError('Kunne ikke sende forslag. Prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFollowClick = () => {
    if (!account) return;
    const cleanHandle = account.instagramUrl.replace('@', '').trim();
    setHasFollowed(true);
    window.open(`https://instagram.com/${cleanHandle}`, '_blank', 'noopener,noreferrer');
  };

  const handleNewSuggestion = () => {
    setShowSuccess(false);
    setMessage('');
    setName('');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] p-6 flex items-center justify-center">
        <p className="text-white text-lg">Laster...</p>
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] p-6">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-[#1a1a1a] border-none">
            <CardContent className="p-6 text-center">
              <h1 className="text-2xl font-bold text-white mb-4">Oops!</h1>
              <p className="text-gray-400">{error}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Closed = closesAt has any value (manual toggle)
  const isClosed = !!account.closesAt;

  if (isClosed) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] p-6 flex items-center justify-center">
        <div className="max-w-2xl mx-auto w-full">
          <Card className="bg-[#1a1a1a] border-none">
            <CardContent className="p-8 text-center">
              {account.profileImageUrl && (
                <img
                  src={account.profileImageUrl}
                  alt={account.displayName}
                  className="w-24 h-24 rounded-full mx-auto mb-6 border-4 border-white/20 object-cover bg-[#2a2a2a]"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
              <h1 className="text-3xl font-bold text-white mb-3">
                Ønsker er nå stengt
              </h1>
              <p className="text-gray-400 text-lg">
                Takk for interessen! Følg @{account.instagramUrl.replace('@', '')} på Instagram for oppdateringer.
              </p>
            </CardContent>
          </Card>
          <p className="text-center text-gray-600 text-xs mt-6">Scenechat</p>
        </div>
      </div>
    );
  }

  // Instagram follow gate
  if (!hasFollowed) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] p-6">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-[#1a1a1a] border-none">
            <CardContent className="p-6 text-center">
              <div className="mb-8">
                {account.profileImageUrl && (
                  <img
                    src={account.profileImageUrl}
                    alt={account.displayName}
                    className="w-24 h-24 sm:w-32 sm:h-32 rounded-full mx-auto mb-6 border-4 border-white object-cover bg-[#2a2a2a]"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                <h1 className="text-2xl font-bold text-white mb-4">
                  Følg {account.displayName} på Instagram
                </h1>
                <p className="text-gray-400 mb-6">
                  Følg @{account.instagramUrl.replace('@', '')} på Instagram for å sende forslag!
                </p>
              </div>
              <Button
                onClick={handleFollowClick}
                className="w-full bg-white text-black font-semibold py-4 sm:py-6 hover:bg-gray-200"
              >
                Følg på Instagram
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-6">
      <div className="max-w-2xl mx-auto">
        <Card className="bg-[#1a1a1a] border-none">
          <CardContent className="p-6">
            <h1 className="text-2xl font-bold text-white mb-2">
              {account.displayName}
            </h1>
            <p className="text-gray-400 mb-6">Hva vil du se på scenen?</p>

            {showSuccess ? (
              <div className="text-center py-8">
                <div className="bg-green-500/10 border border-green-500/20 text-green-400 px-4 py-8 rounded-lg mb-6">
                  <h3 className="text-2xl font-bold mb-2">Takk!</h3>
                  <p className="text-lg">Forslaget ditt er sendt.</p>
                </div>
                <Button
                  onClick={handleNewSuggestion}
                  className="bg-white text-black font-semibold py-4 px-8 hover:bg-gray-200"
                >
                  Send et nytt forslag
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}
                <div>
                  <label htmlFor="message" className="block text-white text-sm mb-2">
                    Beskriv en scene du vil se
                  </label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="F.eks. En date som går helt galt..."
                    className="w-full p-4 rounded-lg bg-[#2a2a2a] text-white border-none focus:ring-2 focus:ring-white/50 placeholder-gray-500 min-h-[100px] resize-none"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label htmlFor="name" className="block text-white text-sm mb-2">
                    Ditt navn (valgfritt)
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Skriv navnet ditt"
                    className="w-full p-4 rounded-lg bg-[#2a2a2a] text-white border-none focus:ring-2 focus:ring-white/50 placeholder-gray-500"
                    disabled={isSubmitting}
                  />
                  <p className="text-gray-500 text-xs mt-1.5">Unngå etternavn og personopplysninger. Fornavn er valgfritt.</p>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-white text-black font-semibold py-4 sm:py-6 hover:bg-gray-200"
                  disabled={isSubmitting || !message.trim()}
                >
                  {isSubmitting ? 'Sender...' : 'Send forslag'}
                </Button>
                <p className="text-center text-gray-600 text-xs mt-3">
                  Ved å sende inn godtar du vår <a href="/personvern" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-400">personvernerklæring</a>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
        <p className="text-center text-gray-600 text-xs mt-6">Scenechat</p>
      </div>
    </div>
  );
};
